import React, { useContext, useEffect, useRef } from 'react';
import classNames from 'classnames';
import { Field, FormSpy, useForm, useFormState } from 'react-final-form';
import InputField from 'components/InputField';
import { useTranslation } from 'react-i18next';
import validations from 'validations';
import HintMessage from 'components/HintMessage';
import RadioSliderField from 'components/RadioSliderField';
import RadioGroupField from 'components/RadioGroupField';
import useMatchMedia from 'hooks/useMatchMedia';
import useOperationStepValues from 'router/data/useOperationStepValues';
import { WhenTwoFieldChanges } from 'router/subrouters/ListingCreationWizard/components/WhenTwoFieldChanges';
import {
  calculatePriceArraySum,
  calculatePriceDifference,
  calculatePriceDivision,
} from 'router/subrouters/ListingCreationWizard/utils';
import { Listing, PriceDescription } from 'models/Listing';
import useCallbackRef from 'hooks/useCallbackRef';
import { useLocation } from 'react-router-dom';
import useHeightAnimation from 'hooks/useHeightAnimation';
import { AdjustmentsArray } from '../../components/AdjustmentsArray';
import {
  ASKING_PRICE_SELECT,
  AskingPriceSelect,
  STOCK_AT_VALUE,
  StockAtValue,
} from 'models/DealRoom';
import { useGenerateSliderOptions } from '../../hooks/useGenerateSliderOptions';
import ControlledInformationCard from 'components/ControlledInformationCard';
import { OwnerOfBusiness } from 'router/subrouters/ListingCreationWizard/components/OwnerOfBusiness';
import { EnableTeaser } from 'router/subrouters/ListingCreationWizard/components/EnableTeaser';
import PatchListingContext from '../../providers/PatchListing/PatchListing.context';
import { EDIT_ROUTE } from 'data/global';

import './StatementOfInformation.styles.scss';
import './StatementOfInformation.styles.responsive.scss';

type StatementOfInformationProps = {
  className?: string;
};

const StatementOfInformation: React.FC<StatementOfInformationProps> = (
  props,
) => {
  const { className } = props;
  const { t } = useTranslation();
  const {
    descriptionPriceOptions,
    accountingPeriodOptions,
    stockAtValueOptions,
  } = useGenerateSliderOptions();
  const { priceGuideOptions } = useOperationStepValues();

  const { patchListingValue } = useContext(PatchListingContext);

  const isFirstRender = useRef(true);

  useEffect(() => {
    isFirstRender.current = false;
  });

  const classes = classNames('statement-of-information', 'lcw-page', className);
  const [content, contentRef] = useCallbackRef<HTMLDivElement>();

  const isMobile = useMatchMedia('isMobile');
  const isEditing = useLocation().pathname.includes(EDIT_ROUTE);

  const { change } = useForm();
  const { values: listing } = useFormState<Listing>();
  const { askingPriceSelect, stockAtValue } = listing;

  const isFixedPrice = askingPriceSelect === ASKING_PRICE_SELECT.FIXED_PRICE;
  const hasStockAtValue = stockAtValue !== STOCK_AT_VALUE.NO_STOCK_AT_VALUE;

  useHeightAnimation({
    element: content,
    defaultOpen: true,
  });

  return (
    <div className={classes}>
      <p className="lcw-page__heading__title">{t('statementOfInformation')}</p>

      <HintMessage
        message={
          <div className="statement-of-information__hint-message">
            <p>
              {t('noDetailsOnTheStatementOfInformationIdentifyTheBusiness')}
            </p>
            <p>{t('soiCriteriaMessage')}</p>
            <ul>
              <li>{t('areLoggedIn')}</li>
              <li>{t('haveVerifiedTherAccount')}</li>
              <li>{t('haveEnquiredOnTheListing')}</li>
            </ul>
            <p>
              {t(
                'priceInformationEnablesResolveToMatchListingsToPriceFilteredSearch',
              )}
            </p>
          </div>
        }
      />

      <div
        className={classNames(
          'white-panel',
          'statement-of-information__price-guide',
        )}
      >
        <p className="statement-of-information__title">{t('priceGuide')}</p>
        <div
          className={classNames(
            'lcw-page__field-row',
            'statement-of-information__sale-price',
          )}
        >
          {!isFixedPrice ? (
            <div className="lcw-page__field-column">
              <Field
                name="amountFrom"
                label={t('amountFrom')}
                numberStyle="dottedNumber"
                placeholder="$"
                required
                component={InputField}
                validate={validations.composeValidators(
                  validations.required(),
                  validations.positiveNumbersOnly(
                    t('onlyPositiveNumbersAreAllowed'),
                  ),
                )}
                customOnBlur={(
                  e: React.FocusEvent<HTMLInputElement>,
                  isValid: boolean,
                ) => {
                  if (isValid) {
                    patchListingValue({ amountFrom: e.target.value });
                  }
                }}
              />
              <Field
                name="amountTo"
                label={t('amountTo')}
                numberStyle="dottedNumber"
                placeholder="$"
                required
                component={InputField}
                validate={validations.composeValidators(
                  validations.required(),
                  validations.positiveNumbersOnly(
                    t('onlyPositiveNumbersAreAllowed'),
                  ),
                  validations.isGreatherOrEqualThanField('amountFrom'),
                )}
                customOnBlur={(
                  e: React.FocusEvent<HTMLInputElement>,
                  isValid: boolean,
                ) => {
                  if (isValid) {
                    patchListingValue({ amountTo: e.target.value });
                  }
                }}
              />

              {askingPriceSelect ===
                ASKING_PRICE_SELECT.EXPRESSION_OF_INTEREST && (
                <p className="eoi-description">
                  {t('askingPriceEoiDescription')}
                </p>
              )}
            </div>
          ) : (
            <Field
              name="amount"
              label={t('salePrice')}
              numberStyle="dottedNumber"
              placeholder="$"
              required
              component={InputField}
              validate={validations.composeValidators(
                validations.required(),
                validations.greatherThanZero(
                  t('onlyPositiveNumbersAreAllowed'),
                ),
              )}
              customOnBlur={(
                e: React.FocusEvent<HTMLInputElement>,
                isValid: boolean,
              ) => {
                if (isValid) {
                  patchListingValue({ amount: e.target.value });
                }
              }}
            />
          )}

          <Field
            name="askingPriceSelect"
            slideSteps={priceGuideOptions}
            component={RadioSliderField}
            customOnChange={(askingPriceSelect: AskingPriceSelect) =>
              patchListingValue(
                {
                  askingPriceSelect,
                },
                { amount: null, amountFrom: null, amountTo: null },
              )
            }
          />
        </div>
        <div>
          <p className="statement-of-information__title">
            {t('financialStatement')}
          </p>
          <p className="statement-of-information__description">
            {t('financialStatementDescription')}
          </p>
        </div>
        <Field
          name="revenue"
          className="statement-of-information__row-input-field"
          numberStyle="dottedNumber"
          placeholder="$"
          component={InputField}
          label={
            <>
              {t('revenueUC')}{' '}
              <span className="required-field">{t('(required)')}</span>
            </>
          }
          validate={validations.composeValidators(
            validations.required(),
            validations.positiveNumbersOnly(t('onlyPositiveNumbersAreAllowed')),
            validations.isGreatherOrEqualThanField('COGS'),
          )}
          customOnBlur={(
            e: React.FocusEvent<HTMLInputElement>,
            isValid: boolean,
          ) => {
            if (isValid) {
              patchListingValue({ revenue: e.target.value });
            }
          }}
        />
        <Field
          name="COGS"
          label={t('cogs')}
          className="statement-of-information__row-input-field"
          numberStyle="dottedNumber"
          placeholder="$"
          component={InputField}
          validate={validations.positiveNumbersOnly(
            t('onlyPositiveNumbersAreAllowed'),
          )}
          customOnBlur={(
            e: React.FocusEvent<HTMLInputElement>,
            isValid: boolean,
          ) => {
            if (isValid) {
              patchListingValue({ COGS: e.target.value });
            }
          }}
        />
        <span className="lcw-page__field-border" />
        <WhenTwoFieldChanges
          name="grossProfit"
          nameFrom="revenue"
          nameTo="COGS"
          calculateNewValue={calculatePriceDifference}
        />
        <Field
          name="grossProfit"
          numberStyle="dottedNumber"
          placeholder="$"
          label={t('grossProfit')}
          className="statement-of-information__row-input-field"
          readOnly
          component={InputField}
          validate={validations.positiveNumbersOnly(
            t('onlyPositiveNumbersAreAllowed'),
          )}
        />
        <span className="lcw-page__field-border" />
        <Field
          name="lessOverheads"
          numberStyle="dottedNumber"
          placeholder="$"
          label={t('operatingExpenses')}
          className="statement-of-information__row-input-field"
          component={InputField}
          validate={validations.composeValidators(
            validations.positiveNumbersOnly(t('onlyPositiveNumbersAreAllowed')),
            validations.isLowerThanField('grossProfit'),
          )}
          customOnBlur={(
            e: React.FocusEvent<HTMLInputElement>,
            isValid: boolean,
          ) => {
            if (isValid) {
              patchListingValue({ lessOverheads: e.target.value });
            }
          }}
        />
        <span className="lcw-page__field-border" />
        <WhenTwoFieldChanges
          name="operatingProfit"
          nameFrom="grossProfit"
          nameTo="lessOverheads"
          calculateNewValue={calculatePriceDifference}
        />
        <Field
          name="operatingProfit"
          numberStyle="dottedNumber"
          placeholder="$"
          label={t('operatingProfitUC')}
          className="statement-of-information__row-input-field"
          readOnly
          component={InputField}
          validate={validations.positiveNumbersOnly(
            t('onlyPositiveNumbersAreAllowed'),
          )}
        />
        <span className="lcw-page__field-border" />
        <Field
          name="EBITDA"
          numberStyle="dottedNumber"
          placeholder="$"
          className="statement-of-information__row-input-field"
          label={
            <>
              {t('ebitda')}{' '}
              <span className="required-field">{t('(required)')}</span>
            </>
          }
          component={InputField}
          validate={validations.composeValidators(
            validations.required(),
            validations.validDecimalNumbersOnlyValidation(
              t('numbersOnlyError'),
            ),
          )}
          customOnBlur={(
            e: React.FocusEvent<HTMLInputElement>,
            isValid: boolean,
          ) => {
            if (isValid) {
              patchListingValue({ EBITDA: e.target.value });
            }
          }}
        />
        <Field
          name="depreciationAndAmortisation"
          numberStyle="dottedNumber"
          className="statement-of-information__row-input-field"
          label={t('depreciationAndAmortisation')}
          placeholder="$"
          component={InputField}
          validate={validations.positiveNumbersOnly(
            t('onlyPositiveNumbersAreAllowed'),
          )}
          customOnBlur={(
            e: React.FocusEvent<HTMLInputElement>,
            isValid: boolean,
          ) => {
            if (isValid) {
              patchListingValue({
                depreciationAndAmortisation: e.target.value,
              });
            }
          }}
        />
        <Field
          name="EBIT"
          className="statement-of-information__row-input-field"
          label={t('ebit')}
          numberStyle="dottedNumber"
          placeholder="$"
          component={InputField}
          validate={validations.positiveNumbersOnly(
            t('onlyPositiveNumbersAreAllowed'),
          )}
          customOnBlur={(
            e: React.FocusEvent<HTMLInputElement>,
            isValid: boolean,
          ) => {
            if (isValid) {
              patchListingValue({ EBIT: e.target.value });
            }
          }}
        />
        <WhenTwoFieldChanges
          name="EBITDAMultiple"
          nameFrom={isFixedPrice ? 'amount' : 'amountFrom'}
          nameTo="EBITDA"
          calculateNewValue={calculatePriceDivision}
        />
        <Field
          name="EBITDAMultiple"
          numberStyle="dottedNumber"
          label={t('ebitdaMultiple')}
          className="statement-of-information__row-input-field"
          placeholder="$"
          readOnly
          component={InputField}
          validate={validations.validDecimalNumbersOnlyValidation(
            t('numbersOnlyError'),
          )}
        />
        <span className="lcw-page__field-border" />
        <AdjustmentsArray />
        <span className="lcw-page__field-border" />
        <FormSpy
          subscription={{ values: true }}
          onChange={({ values }) => {
            if (!isFirstRender.current) {
              const valueArray = values.adjustments?.map(
                (el: { netAmount: string }) => el?.netAmount,
              );
              const oldValue = values.totalAdjustments;
              const newValue = calculatePriceArraySum(valueArray) || 0;

              const isChangedValue = oldValue !== newValue;

              if (valueArray?.length && isChangedValue) {
                change('totalAdjustments', newValue);
              }
            }
          }}
        />
        <Field
          name="totalAdjustments"
          label={t('totalAdjustmentsUC')}
          className="statement-of-information__row-input-field"
          numberStyle="dottedNumber"
          placeholder="$"
          component={InputField}
          readOnly
        />
        <span className="lcw-page__field-border" />
        <div className="lcw-page__field-row">
          <Field
            name="priceDescription"
            label={t('description')}
            slideSteps={descriptionPriceOptions}
            component={RadioSliderField}
            customOnChange={(priceDescription: PriceDescription) =>
              patchListingValue({ priceDescription })
            }
          />

          <RadioGroupField
            name="accountingPeriod"
            options={accountingPeriodOptions}
            label={
              <>
                {t('accountingPeriod')}{' '}
                <span className="required-field">{t('(required)')}</span>
              </>
            }
            customOnChange={(accountingPeriod: string) => {
              patchListingValue({ accountingPeriod });
            }}
          />
        </div>
        <EnableTeaser listing={listing} isEditing={isEditing} />
      </div>

      <div className="white-panel">
        <div className="lcw-page__field-column">
          <p className="statement-of-information__title">{t('stockAtValue')}</p>

          {isMobile ? (
            <RadioGroupField
              name="stockAtValue"
              options={stockAtValueOptions}
              customOnChange={(stockAtValue: StockAtValue) => {
                patchListingValue({ stockAtValue });
              }}
            />
          ) : (
            <Field
              name="stockAtValue"
              slideSteps={stockAtValueOptions}
              component={RadioSliderField}
              validate={validations.required()}
              customOnChange={(stockAtValue: StockAtValue) =>
                patchListingValue({ stockAtValue })
              }
            />
          )}

          <div
            ref={contentRef}
            className={classNames(
              'lcw-page__field-row',
              'statement-of-information__stock',
            )}
          >
            {hasStockAtValue && (
              <Field
                name="averageStockValue"
                label={t('averageStockValue')}
                numberStyle="dottedNumber"
                component={InputField}
                placeholder="$"
                validate={validations.positiveNumbersOnly(
                  t('onlyPositiveNumbersAreAllowed'),
                )}
                description={t(
                  'useEitherTheMostRecentFullCalendarYearFinancialYearOrAverageOfLastTwoYears',
                )}
                customOnBlur={(
                  e: React.FocusEvent<HTMLInputElement>,
                  isValid: boolean,
                ) => {
                  if (isValid) {
                    patchListingValue({
                      averageStockValue: e.target.value || null,
                    });
                  }
                }}
              />
            )}

            <span />
          </div>
        </div>
      </div>

      <OwnerOfBusiness />
      <ControlledInformationCard />
    </div>
  );
};

export default StatementOfInformation;
